import React, { useEffect } from "react";
import { SettingRow } from "../SettingRow";
import { ColorPicker } from "../../../../../../common/components/input/ColorPicker";

export const ICON_PRIMARY_COLOR_DEFAULT = "#123456";
export const ICON_SECONDARY_COLOR_DEFAULT = "#E8E5EE";

interface ConsentBannerIconColorRowProps {
  usBannerEnabled?: boolean;
  canadaBannerEnabled?: boolean;
  eeaukBannerEnabled?: boolean;
  formApi: {
    change: (field: string, value: string) => void;
    getFieldState: (name: string) => undefined | { value: string };
  };
}

const setIconBackgroundColor = (color: string) => {
  Array.from(document.getElementsByClassName("tv-cookie-icon-bg")).map((el) => {
    el.setAttribute("fill", color);
  });
};

const setIconForegroundColor = (color: string) => {
  Array.from(document.getElementsByClassName("tv-cookie-icon-fg")).map((el) => {
    el.setAttribute("fill", color);
  });
};

export const ConsentBannerIconColorRow = ({
  formApi,
  usBannerEnabled,
  canadaBannerEnabled,
  eeaukBannerEnabled,
}: ConsentBannerIconColorRowProps) => {
  useEffect(() => {
    // Set first-render colors for SVG previews
    setIconForegroundColor(formApi.getFieldState("bannerIconColorPrimary")?.value);
    setIconBackgroundColor(formApi.getFieldState("bannerIconColorSecondary")?.value);
  }, [formApi]);
  return (
    <SettingRow
      maxWidthChildren={true}
      title="Icon Color"
      helpText="Choose the color for the consent preferences dialog icon that appears on your site."
    >
      <div>
        <ColorPicker
          className="w-100 mb-sm"
          fullWidth={false}
          field="bannerIconColorPrimary"
          title="Primary Color"
          placeholder="Primary Color"
          label="Primary Color"
          variant="outlined"
          onChange={(event) => {
            setIconForegroundColor(event.target.value);
          }}
          required
        />
      </div>
      <div>
        <ColorPicker
          className="w-100 mb-sm"
          fullWidth={false}
          field="bannerIconColorSecondary"
          title="Secondary Color"
          placeholder="Secondary Color"
          label="Secondary Color"
          variant="outlined"
          required
          onChange={(event) => {
            setIconBackgroundColor(event.target.value);
          }}
        />
      </div>
      {(ICON_PRIMARY_COLOR_DEFAULT !== formApi?.getFieldState("bannerIconColorPrimary")?.value ||
        ICON_SECONDARY_COLOR_DEFAULT !==
          formApi?.getFieldState("bannerIconColorSecondary")?.value) && (
        <div className="mb-md">
          <a
            onClick={(e) => {
              e.preventDefault();
              formApi.change("bannerIconColorPrimary", ICON_PRIMARY_COLOR_DEFAULT);
              setIconForegroundColor(ICON_PRIMARY_COLOR_DEFAULT);
              formApi.change("bannerIconColorSecondary", ICON_SECONDARY_COLOR_DEFAULT);
              setIconBackgroundColor(ICON_SECONDARY_COLOR_DEFAULT);
            }}
            href="#"
          >
            Reset to Default Colors
          </a>
        </div>
      )}
      <div>
        <p className="text-t4 mb-xs">
          <strong>Preview</strong>
        </p>
        {(canadaBannerEnabled || eeaukBannerEnabled) && (
          <div className="flex flex-row align-items-center">
            <div>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="-2 -2 28 28"
                width="30"
                className="tv-cookie-icon"
                id="tvCookieIconEEAUK"
              >
                <circle
                  className="tv-cookie-icon-bg"
                  r="14"
                  fill="#E8E5EE"
                  cx="12"
                  cy="12"
                ></circle>
                <path
                  className="tv-cookie-icon-fg"
                  d="M21.95 10.99c-1.79-.03-3.7-1.95-2.68-4.22-2.98 1-5.77-1.59-5.19-4.56C6.95.71 2 6.58 2 12c0 5.52 4.48 10 10 10 5.89 0 10.54-5.08 9.95-11.01M8.5 15c-.83 0-1.5-.67-1.5-1.5S7.67 12 8.5 12s1.5.67 1.5 1.5S9.33 15 8.5 15m2-5C9.67 10 9 9.33 9 8.5S9.67 7 10.5 7s1.5.67 1.5 1.5-.67 1.5-1.5 1.5m4.5 6c-.55 0-1-.45-1-1s.45-1 1-1 1 .45 1 1-.45 1-1 1"
                  fill="#123456"
                ></path>
              </svg>
            </div>
            <div className="w-128 text-t2 text-weight-bold text-muted pb-xs">
              {[eeaukBannerEnabled ? "EEA/UK/CH" : null, canadaBannerEnabled ? "Canada" : null]
                .filter(Boolean)
                .join(" + ")}
            </div>
          </div>
        )}
        {usBannerEnabled && (
          <div className="flex flex-row align-items-center">
            <div>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                width="30"
                className="tv-cookie-icon"
                id="tvCookieIconUS"
              >
                <circle
                  className="tv-cookie-icon-bg"
                  r="12"
                  fill="#E8E5EE"
                  cx="12"
                  cy="12"
                ></circle>
                <path
                  className="tv-cookie-icon-fg"
                  fillRule="evenodd"
                  fill="#123456"
                  d="m6.25 11.5 0 0 -2.4 -2.4 1 -1 2.35 2.35 4.4 -4.4 1 1 -5.4 5.4z M 3.5 20.5 A 12 12 0 0 1 20.5 3.5 z"
                ></path>
                <path
                  className="tv-cookie-icon-fg"
                  fillRule="evenodd"
                  fill="#123456"
                  d="m16 15 2 -2 1 1 -2 2 2 2 -1 1 -2 -2 -2 2 -1 -1 2 -2 -2 -2 1 -1z"
                ></path>
              </svg>
            </div>
            <div className="w-128 text-t2 text-weight-bold text-muted pb-xs">US</div>
          </div>
        )}
      </div>
    </SettingRow>
  );
};
